import autobind from 'class-autobind';
import * as React from 'react';
import { compose } from 'recompose';
import getClientEnvironment from '../getClientEnvironment';
import {
    AnimationContextOptions,
    AnimationContextProps,
    CompositeComponent
} from './types';
import { mod } from './utils';
import withAnimationFrame, {
    AnimationFrameComponentProps
} from './withAnimationFrame';

const config = getClientEnvironment();

const defaultState: AnimationContextOptions = {
    /** Duration of the animation in ms */
    duration: config.ANIMATION_DURATION * 1000,
    /** Scales the duration in response to changes in zoom levels */
    durationScale: 1,
    /** Indicates whether the animation is moving */
    isAnimating: false,
    /** Progress of the animation from 0 to 1 */
    progress: 0
};

const defaultContext: AnimationContextProps = {
    ...defaultState,
    onAnimationChanged: (options: Partial<AnimationContextOptions>) => {
        return;
    }
};

/** Animation context which is exposed via the HOCs */
const AnimationContext = React.createContext<AnimationContextProps>(
    defaultContext
);

interface AnimationContextProviderState extends AnimationContextOptions {
    time?: number;
}

/** Allows the inner component to consume animation props */
export default <TProps extends {}>(
    InnerComponent: CompositeComponent<TProps & AnimationContextProps>
) => (props: TProps) => (
    <AnimationContext.Consumer>
        {animationContext => (
            <InnerComponent {...animationContext} {...props} />
        )}
    </AnimationContext.Consumer>
);

const withAnimationContextProviderCore = <TProps extends {}>(
    InnerComponent: CompositeComponent<TProps>
) =>
    class AnimationContextProvider extends React.Component<
        AnimationFrameComponentProps,
        AnimationContextProviderState
    > {
        constructor(props: AnimationFrameComponentProps) {
            super(props);

            autobind(this);

            this.state = { ...defaultState };
        }

        public componentDidMount() {
            const { startAnimation, endAnimation } = this.props;
            this.state.isAnimating ? startAnimation() : endAnimation();
        }

        public render() {
            // Strip out the startAnimation and endAnimation callbacks,
            // because we don't want to directly expose them.
            const { startAnimation, endAnimation, ...otherProps } = this.props;

            const {
                isAnimating,
                progress,
                duration,
                durationScale
            } = this.state;

            const contextValue: AnimationContextProps = {
                duration,
                durationScale,
                isAnimating,
                onAnimationChanged: this.handleAnimationChanged,
                progress
            };

            return (
                <AnimationContext.Provider value={contextValue}>
                    <InnerComponent {...otherProps} />
                </AnimationContext.Provider>
            );
        }

        public componentDidUpdate(
            { startAnimation, endAnimation }: AnimationFrameComponentProps,
            { isAnimating: prevIsAnimating }: AnimationContextProviderState
        ) {
            const { isAnimating } = this.state;
            if (isAnimating !== prevIsAnimating) {
                isAnimating ? startAnimation() : endAnimation();
            }
        }

        public onAnimationFrame(time: number) {
            this.setState(
                ({
                    isAnimating,
                    progress: prevProgress,
                    duration,
                    durationScale,
                    time: prevTime
                }: AnimationContextProviderState) => {
                    if (!isAnimating) {
                        return null;
                    }

                    const elapsedSeconds = (time - (prevTime || time)) / 1000;
                    const speed = 1 / ((duration * durationScale) / 1000);
                    const distance = elapsedSeconds * speed;
                    const progress = mod(prevProgress + distance, 1);

                    return { progress, time };
                }
            );
        }

        private removeUndefinedProperties(obj: any) {
            const clone = { ...obj };

            Object.getOwnPropertyNames(clone).forEach(
                p => typeof clone[p] === 'undefined' && delete clone[p]
            );

            return clone;
        }

        private handleAnimationChanged(
            options: Partial<AnimationContextOptions>
        ) {
            // Make sure there's no funny business with the internal state
            if (
                typeof options.progress !== 'undefined' &&
                (options.progress > 1 || options.progress < 0)
            ) {
                throw new Error('Progress must be between 0 and 1 inclusive');
            }

            if (
                typeof options.duration !== 'undefined' &&
                options.duration <= 0
            ) {
                throw new Error('Duration must be greater than zero');
            }

            if (
                typeof options.durationScale !== 'undefined' &&
                options.durationScale <= 0
            ) {
                throw new Error('Duration scale must be greater than zero');
            }

            const cleanOptions = this.removeUndefinedProperties(options);

            this.setState(({ isAnimating: prevIsAnimating }) => {
                if (
                    typeof cleanOptions.isAnimating !== 'undefined' &&
                    prevIsAnimating !== options.isAnimating &&
                    !options.isAnimating
                ) {
                    return { ...cleanOptions, time: undefined };
                }

                return cleanOptions;
            });
        }
    };

export const withAnimationContextProvider = compose<any, AnimationContextProps>(
    withAnimationFrame<any>(),
    withAnimationContextProviderCore
);

// Argument of type '(prevState: AnimationContextProviderState, props: AnimationFrameComponentProps) => Partial<Animat...' is not assignable to parameter of type 'AnimationContextProviderState | ((prevState: Readonly<AnimationContextProviderState>, props: Anim...'.
//   Type '(prevState: AnimationContextProviderState, props: AnimationFrameComponentProps) => Partial<Animat...' is not assignable to type 'Pick<AnimationContextProviderState, "duration" | "durationScale" | "isAnimating" | "progress" | "...'.
//     Property 'duration' is missing in type '(prevState: AnimationContextProviderState, props: AnimationFrameComponentProps) => Partial<Animat...'.



// WEBPACK FOOTER //
// ./src/client/components/withAnimationContext.tsx